import React from 'react';

import moment from 'moment-timezone';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Tag } from '../../../common/components/Tag';
import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ProductFactory } from '../../../common/productConfig/productFactory';
import { ITask } from '../../tasks/interfaces/ITask';
import {
  CriminalRecords,
  CriminalRecordsFacts,
} from '../../tasks/types/criminalRecords';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { RetryableEvidenceItem } from './components/RetryableEvidenceItem';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

export const CriminalRecordsDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  retryHandler,
  markOutdatedHandler,
  product,
  carrier,
  subCategory,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  retryHandler?: (taskId: number) => void;
  markOutdatedHandler: (taskId: number) => void;
  product: string;
  carrier: string;
  subCategory: string;
}) => {
  const data: CriminalRecords = task.taskData as CriminalRecords;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  const facts: CriminalRecordsFacts = task.evidence
    ?.facts as CriminalRecordsFacts;
  const errorMessage = task.evidence?.errorMessage;

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  return (
    <Box data-label={ElementLocators.LOCATOR_CRIMINAL_RECORDS_SECTION}>
      {!productConfig?.automatedCriminalRecordsAvailable() && (
        <>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            ml={-1}
            id={task.id.toString()}
          >
            <Box display="flex" flexDirection="row">
              <Box mt={0.5}>
                <NoraTaskIcon
                  task={task}
                  onTaskStateClick={onTaskStateChange}
                />
              </Box>
              <Typography variant="h2">
                {Resource.TASK_TITLE_CRIMINAL_RECORDS}
              </Typography>
            </Box>
            <Box>
              <Typography color="textSecondary" variant="subtitle2">
                {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
              </Typography>
            </Box>
          </Box>
          {task.state === TaskState.WAIVED && task.waiveReason && (
            <FlexRow className={styles.TaskSubtitle}>
              <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
            </FlexRow>
          )}
          {evidenceExists(evidence) && evidence && (
            <FileDetail
              documents={evidence.documents}
              fileOpenHandler={downloadHandler}
              fileDeletehandler={(documentId: number, reason: string) =>
                deleteHandler(task, documentId, reason)
              }
            />
          )}

          {task.state !== TaskState.COMPLETED &&
            task.state !== TaskState.REQUESTED && (
              <FileUpload
                leftItem={evidenceExists(evidence) ? '' : Resource.FILES}
                selectedFiles={onFileSelection}
                type={DocumentType.CRIMINAL_RECORDS}
                ongoingUpload={
                  task.ongoingUpload?.[DocumentType.CRIMINAL_RECORDS]
                }
              />
            )}
          {task.state !== TaskState.REQUESTED && (
            <TaskNote taskID={task.id} originalNote={data?.note} />
          )}

          {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
            <>
              <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
              {evidenceReasons?.length > 0 && (
                <TaskDetailsItem
                  leftItem={Resource.EVIDENCE}
                  rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
                />
              )}
              {data?.reasonDescription && (
                <TaskDetailsItem
                  leftItem={Resource.DESCRIPTION}
                  rightItem={data.reasonDescription}
                />
              )}
            </>
          )}
        </>
      )}

      {productConfig?.automatedCriminalRecordsAvailable() && (
        <RetryableEvidenceItem
          task={task}
          documentType={DocumentType.CRIMINAL_RECORDS}
          title={Resource.TASK_TITLE_CRIMINAL_RECORDS}
          downloadHandler={downloadHandler}
          uploadHandler={uploadHandler}
          deleteHandler={deleteHandler}
          retryHandler={retryHandler}
          taskStateHandler={taskStateHandler}
          markOutdatedHandler={markOutdatedHandler}
        >
          {!errorMessage && facts && (
            <>
              <Box mt={2}>
                <TaskDetailsItem
                  leftItem={Resource.CRIMINAL_RECORDS_RESULT}
                  rightItem={
                    facts.recordExists ? (
                      <Tag color="LighterBlue">HIT</Tag>
                    ) : (
                      <Tag color="LightOrange">NO HIT</Tag>
                    )
                  }
                />
              </Box>

              {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
                <>
                  <FlexRow className={styles.TaskSubtitle}>
                    {Resource.REASON}
                  </FlexRow>
                  {evidenceReasons?.length > 0 && (
                    <TaskDetailsItem
                      leftItem={Resource.EVIDENCE}
                      rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
                    />
                  )}
                  {data?.reasonDescription && (
                    <TaskDetailsItem
                      leftItem={Resource.DESCRIPTION}
                      rightItem={data.reasonDescription}
                    />
                  )}
                </>
              )}
            </>
          )}

          {!errorMessage && facts?.recordExists && (
            <Box mt={4}>
              <TableContainer component={Paper} variant="outlined" square>
                <Table aria-label="Decisions table">
                  <TableHead>
                    <TableRow>
                      <TableCell width={120}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_CATEGORY}
                        </Typography>
                      </TableCell>
                      <TableCell width={130}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SUBCATEGORY}
                        </Typography>
                      </TableCell>
                      <TableCell width={160}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DESCRIPTION}
                        </Typography>
                      </TableCell>
                      <TableCell width={100}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SEVERITY}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DISPOSITION}
                        </Typography>
                      </TableCell>
                      <TableCell width={120}>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_DATE}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="overline" color="textSecondary">
                          {Resource.CRIMINAL_RECORDS_SOURCE}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facts.offenses.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Typography variant="body1">
                            {row.categoryName ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.offenseSubcategoryName ||
                              row.offenseSubcategoryCode) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.description ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.severityDescription || row.severityType) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(row.dispositionDescription ||
                              row.dispositionType) ??
                              Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2">
                              {row.recordDateType}
                            </Typography>
                            <Typography variant="body2">
                              {row.recordDate
                                ? moment(row.recordDate, 'YYYY-MM-DD').format(
                                    'YYYY/MM/DD',
                                  )
                                : Resource.NORA_M_DASH}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.source ?? Resource.NORA_M_DASH}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </RetryableEvidenceItem>
      )}
    </Box>
  );
};
